import { ThemeProvider } from "@mui/material";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useMemo } from "react";
import { Provider, useSelector } from "react-redux";

import {
  selectIsAuthenticated,
  selectIsLoadingAuth,
  selectUserEmail,
  store,
} from "~/store";
import { Toaster } from "./components/ui";
import { ApiClientProvider } from "./lib/api/axios-client";
import { queryClient } from "./lib/react-query-client";
import { theme } from "./lib/theme/mui-theme";
import { router } from "./router";

interface ContextRouterProps {
  queryClient: QueryClient;
}

function ContextRouter({ queryClient }: ContextRouterProps) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userEmail = useSelector(selectUserEmail);
  const isLoadingAuth = useSelector(selectIsLoadingAuth);

  const routerContext = useMemo(
    () => ({
      queryClient: queryClient,
      authState: {
        isAuthenticated,
        userEmail,
        isLoadingAuth,
      },
    }),
    [queryClient, isAuthenticated, userEmail, isLoadingAuth],
  );

  return <RouterProvider router={router} context={routerContext} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <ApiClientProvider baseURL={import.meta.env.VITE_API_URL || ""}>
          <Provider store={store}>
            <Toaster />
            <ContextRouter queryClient={queryClient} />
          </Provider>
        </ApiClientProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
